<template>
	<div class="content-page content-page--parts-settings">
		<ActionSetNav
			v-if="!loading"
			:name="this.$route.params.company.advanced.companyName"
			:display="true"
			:actions="mainNavActions"
		/>
		<div class="content-page__heading">
			<div class="content-page__breadcrumb-title">
				<Breadcrumbs :items="urlNames" />
				<h2 class="content-page__title">
					Parts Locations
				</h2>
			</div>
			<div class="content-page__actions">
				<MenuButton
					:iconBeforeLabel="true"
					iconClassName="icon-more"
					:items="locationTypes"
					v-model="locationSelected"
				/>
			</div>
		</div>
		<ScrollContainer
			v-if="!loading"
			class="content-page__content"
			:selectorArray="pageElements"
			:extraSpace="64"
		>
			<!-- <CompanyContent :data="companyLocalData" /> -->
			<Tabs
				:tabs="locationsTabs"
				v-if="partsLocationsLocalData.length > 1"
			>
				<template
					v-for="(item, index) in partsLocationsLocalData"
					#[slotName(index)]
				>
					{{ item.name }}
				</template>
				<template
					v-for="(item, index) in partsLocationsLocalData"
					#[`content${slotName(index)}`]
				>
					<div
						class="parts-locations parts-locations--multiple"
						:key="index"
					>
						<Toggle
							v-model="expandAllSections[index]"
							@change="handleExpandSections(index)"
							label="Expand All Details"
							data-test="expandAllToggle"
						>
							<template slot="true-value">On</template>
							<template slot="false-value">Off</template>
						</Toggle>
						<!-- DEV NOTE: these first two sections (address and information) are also used in company settings so make sure the logic to save the data on this section outside these components -->
						<!-------- ADDRESS SECTION -------->
						<CompanyLocation
							ref="companyLocation"
							:data="item.data.location"
						/>
						<CompanyAddress
							ref="companyAddress"
							:data="item.data.address"
						/>
						<!-------- CONTACT INFORMATION SECTION -------->
						<CompanyInformation
							ref="companyInformation"
							:data="item.data.contactInfo"
						/>
						<!-------- GENERAL SECTION -------->
						<PartsLocationsGeneral
							ref="partsLocationGeneral"
							:data="item.data.general"
						/>
						<PartsLocationsPrinting
							ref="partsLocationPrinting"
							:data="item.data.printing"
						/>
						<PartsLocationsSignature
							ref="partsLocationSignature"
							:data="item.data.signatureCaptureMethops"
						/>
						<PartsLocationsScanner
							ref="partsLocationScanner"
							:data="item.data.scannerSettings"
						/>
						<PartsLocationsSmsEmail
							ref="partsLocationSmsEmail"
							:data="item.data.smsEmail"
						/>
					</div>
				</template>
			</Tabs>
			<div class="parts-locations parts-locations--single" v-else>
				<Toggle
					v-model="expandAllSectionsSingle"
					@change="handleExpandSectionsSingle"
					label="Expand All Details"
					data-test="expandAllToggle"
				>
					<template slot="true-value">On</template>
					<template slot="false-value">Off</template>
				</Toggle>
				<!-------- GENERAL SECTION -------->
				<PartsLocationsGeneral
					ref="partsLocationGeneral"
					:data="partsLocationsLocalData[0].data.general"
				/>
				<PartsLocationsPrinting
					ref="partsLocationPrinting"
					:data="partsLocationsLocalData[0].data.printing"
				/>
				<PartsLocationsSignature
					ref="partsLocationSignature"
					:data="
						partsLocationsLocalData[0].data.signatureCaptureMethops
					"
				/>
				<PartsLocationsScanner
					ref="partsLocationScanner"
					:data="partsLocationsLocalData[0].data.scannerSettings"
				/>
				<PartsLocationsSmsEmail
					ref="partsLocationSmsEmail"
					:data="partsLocationsLocalData[0].data.smsEmail"
				/>
			</div>
		</ScrollContainer>
	</div>
</template>

<script>
import ActionSetNav from '@/site-components/actions-set/action-set-nav';
import Breadcrumbs from '@/components/shells/breadcrumbs/breadcrumbs';
import MenuButton from '@/components/forms/button/menu-button';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import Tabs from '@/components/shells/tabs/tabs';
import CompanyLocation from '@/sections/companies/default-settings/sections/company-location';
import CompanyAddress from '@/sections/companies/default-settings/sections/company-address';
import CompanyInformation from '@/sections/companies/default-settings/sections/company-information';
import PartsLocationsGeneral from './sections/parts-locations-general';
import PartsLocationsPrinting from './sections/parts-locations-printing';
import PartsLocationsSignature from './sections/parts-locations-signature';
import PartsLocationsScanner from './sections/parts-locations-scanner';
import PartsLocationsSmsEmail from './sections/parts-locations-sms-email';
import { ApiService as api } from '@/services/axiosHelper';
import Toggle from '@/components/forms/selection-control/toggle/toggle';

export default {
	name: 'PartsLocations',
	components: {
		ActionSetNav,
		Breadcrumbs,
		MenuButton,
		ScrollContainer,
		Tabs,
		CompanyLocation,
		CompanyAddress,
		CompanyInformation,
		PartsLocationsGeneral,
		PartsLocationsPrinting,
		PartsLocationsSignature,
		PartsLocationsScanner,
		PartsLocationsSmsEmail,
		Toggle
	},
	data() {
		return {
			expandAllSections: [],
			expandAllSectionsSingle: true,
			pageElements: [
				'header.site__header',
				'nav.action-set-nav',
				'footer.site__footer',
				'.content-page__heading'
			],
			loading: false,
			partsLocationsLocalData: [],
			locationsTabs: [],
			locationTypes: [
				{
					text: 'Parts',
					value: 'P'
				},
				{
					text: 'Sales',
					value: 'S'
				},
				{
					text: 'Service',
					value: 'V'
				}
			],
			locationSelected: { text: 'Add Location', value: '' },
			mainNavActions: [
				{
					id: 1,
					action: {
						name: 'accounting'
					},
					title: 'Select to go to Accounting page',
					iconClass: 'icon icon-manage',
					label: 'Accounting',
					type: 'route-link',
					visible: true
				},
				{
					id: 2,
					action: {
						name: 'payroll'
					},
					title: 'Select to go to Payroll page',
					iconClass: 'icon icon-error',
					label: 'Payroll',
					type: 'route-link',
					visible: true
				},
				{
					id: 3,
					action: {
						name: 'parts'
					},
					iconClass: 'icon icon-customer-integration',
					label: 'Parts',
					title: 'Select to go to Parts Locations page',
					type: 'route-link',
					visible: true
				},
				{
					id: 4,
					action: '#',
					disabled: false,
					iconClass: 'icon icon-menu',
					label: '',
					title: 'select to see the more actions',
					type: 'dropdown',
					visible: true,
					items: [
						{
							id: 5,
							action: {
								name: 'sales'
							},
							disabled: false,
							iconClass: 'icon icon-document',
							label: 'Sales',
							title: 'Select to go to the Sales page',
							type: 'route-link',
							visible: true
						},
						{
							id: 6,
							action: {
								name: 'service'
							},
							disabled: false,
							iconClass: 'icon icon-document',
							label: 'Service',
							title: 'Select to go to the Service page',
							type: 'route-link',
							visible: true
						}
					]
				}
			],
			urlNames: [
				{
					name: 'Clients',
					data: {
						path: '/clients'
					}
				},
				{
					name: this.$route.params.company.advanced.companyName,
					data: {
						path: '/clients/:id/companies'
					}
				},
				{
					name: 'Parts Locations'
				}
			]
		};
	},
	created() {
		this.loading = true;
		this.fetchPartsLocations();
	},
	methods: {
		slotName(index) {
			return index + 1;
		},
		/* DEV NOTE: use VUEX */
		async fetchPartsLocations() {
			try {
				const results = await api.get(
					`parts-locations/${this.$route.params.id}`
				);

				results.forEach(element => {
					this.expandAllSections.push(true);

					this.partsLocationsLocalData.push({
						name: element.name,
						data: element.data
					});
					this.locationsTabs.push({
						active: false,
						disabled: false
					});
					this.locationsTabs[0].active = true;
				});
			} catch (error) {
				console.log(error);
			} finally {
				this.loading = false;
			}
		},
		handleExpandSections(index) {
			this.$refs.companyAddress[
				index
			].$children[0].open = this.expandAllSections[index];
			this.$refs.companyLocation[
				index
			].$children[0].open = this.expandAllSections[index];
			this.$refs.companyInformation[
				index
			].$children[0].open = this.expandAllSections[index];
			this.$refs.partsLocationGeneral[
				index
			].$children[0].open = this.expandAllSections[index];
			this.$refs.partsLocationPrinting[
				index
			].$children[0].open = this.expandAllSections[index];
			this.$refs.partsLocationSignature[
				index
			].$children[0].open = this.expandAllSections[index];
			this.$refs.partsLocationScanner[
				index
			].$children[0].open = this.expandAllSections[index];
			this.$refs.partsLocationSmsEmail[
				index
			].$children[0].open = this.expandAllSections[index];
		},
		handleExpandSectionsSingle() {
			this.$refs.partsLocationGeneral.$children[0].open = this.expandAllSectionsSingle;
			this.$refs.partsLocationPrinting.$children[0].open = this.expandAllSectionsSingle;
			this.$refs.partsLocationSignature.$children[0].open = this.expandAllSectionsSingle;
			this.$refs.partsLocationScanner.$children[0].open = this.expandAllSectionsSingle;
			this.$refs.partsLocationSmsEmail.$children[0].open = this.expandAllSectionsSingle;
		}
	}
};
</script>

<style lang="scss" scoped>
@import '@/scss/5-objects/_cards.scss';
.content-page {
	&__heading {
		display: flex;
		align-items: center;
	}
}

.parts-locations {
	@include card;
	height: auto;
}
</style>
